import React, { lazy, useEffect, Suspense } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { themeChange } from "theme-change";
import checkAuth from "./app/auth";
import initializeApp from "./app/init";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/Toast";
import { PageLoader } from "./components/Loading";
import { useTranslation } from "react-i18next";
import { keepLanguageOn } from "./app/i18n";
import { PublicModulePage } from "./features/modules/ModuleGate";

// Importing pages
const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));
const Apply = lazy(() => import("./pages/Apply"));
const ApplyVerify = lazy(() => import("./pages/ApplyVerify"));
const ApplyStatus = lazy(() => import("./pages/ApplyStatus"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const Consent = lazy(() => import("./pages/Consent"));
const ConsentWithdraw = lazy(() => import("./pages/ConsentWithdraw"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const SecondStepSetup = lazy(() => import("./pages/SecondStepSetup"));
const SecondStepReopen = lazy(() => import("./pages/SecondStepReopen"));
// The church's public page (cmms public-page-plan.md, 2.2): a chunk of its own, apart from the staff app's.
const PublicParish = lazy(() => import("./features/public-parish/PublicParish"));
// Development only: the consent page against fixtures, for checking its layout in each state and language (FR-16.8).
// process.env.NODE_ENV is replaced at build time, so a production build drops this import entirely. Not production
// rather than development: the test build has it too, so the refusing-server guard visits every route there is.
const ConsentPreview = process.env.NODE_ENV !== "production"
  ? lazy(() => import("./features/public-consent/ConsentPreview"))
  : null;

// Initializing different libraries
initializeApp();

// Check for login and initialize axios
const token = checkAuth();

/** Whether the chosen language is kept follows the path, on every route change (app/i18n.js). */
function LanguageKeeping() {
  const { pathname } = useLocation();
  useEffect(() => keepLanguageOn(pathname), [pathname]);
  return null;
}

function App() {
  // The first thing any visitor sees, the consent page included - so it goes through t() like everything after it.
  const { t } = useTranslation();
  useEffect(() => {
    // 👆 daisy UI themes initialization
    themeChange(false);
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <LanguageKeeping />
          <Suspense fallback={<PageLoader message={t("loadingApplication")} />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/register" element={<PublicModulePage module="selfRegistration"><Register /></PublicModulePage>} />
              {/* R3 task 7 - FR-3.9a: the public, unauthenticated application form */}
              {/* Each with its module (feature flags): switched off, the page says so and asks for nothing more. */}
              <Route path="/apply" element={<PublicModulePage module="applications"><Apply /></PublicModulePage>} />
              <Route path="/apply/verify" element={<PublicModulePage module="applications"><ApplyVerify /></PublicModulePage>} />
              <Route path="/apply/status" element={<PublicModulePage module="applications"><ApplyStatus /></PublicModulePage>} />
              {/* R4 task 8 - FR-4.7: public, unauthenticated certificate verification lookup */}
              <Route path="/certificates/verify" element={<PublicModulePage module="registers"><VerifyCertificate /></PublicModulePage>} />
              {/* Consent step A3 - the public consent page, reached from a consent e-mail */}
              <Route path="/consent" element={<Consent />} />
              {/* Consent step A4 - the withdrawal link in a consent receipt */}
              <Route path="/consent/withdraw" element={<ConsentWithdraw />} />
              {/* Account package step 5 - the set-password link in an account's e-mail */}
              <Route path="/set-password" element={<SetPassword />} />
              {/* Two-step sign-in (the account package plan, 6.5, 6.8) - its setup link, and the lock's link */}
              <Route path="/second-step-setup" element={<SecondStepSetup />} />
              <Route path="/second-step-reopen" element={<SecondStepReopen />} />
              {ConsentPreview && <Route path="/consent-preview" element={<ConsentPreview />} />}

                           {/* The public pages (cmms public-page-plan.md, 2.2; answer 10): the default church, and a church by its code */}
              <Route path="/" element={<PublicParish signedIn={!!token} />} />
              <Route path="/am" element={<PublicParish signedIn={!!token} />} />
              <Route path="/sv" element={<PublicParish signedIn={!!token} />} />
              <Route path="/en" element={<PublicParish signedIn={!!token} />} />
              <Route path="/c/:code" element={<PublicParish signedIn={!!token} />} />
              <Route path="/am/c/:code" element={<PublicParish signedIn={!!token} />} />
              <Route path="/sv/c/:code" element={<PublicParish signedIn={!!token} />} />
              <Route path="/en/c/:code" element={<PublicParish signedIn={!!token} />} />

              <Route path="/app/*" element={<Layout />} />

              <Route
                path="*"
                element={
                  <Navigate to={token ? "/app/welcome" : "/login"} replace />
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
